import { useKeycloak } from "@react-keycloak/web";
import { KeycloakResourceAccess, KeycloakTokenParsed } from "keycloak-js";
import { useMemo } from "react";

export interface CustomResources extends KeycloakResourceAccess {
  api: {
    roles: string[];
  };
}

export interface TokenWithAttributes extends KeycloakTokenParsed {
  attributes: {
    modulosAcesso: string[];
  };
}

export const useHasPermission = (permission: string): boolean => {
  const { keycloak } = useKeycloak();

  const hasPermission = useMemo(() => {
    if (!keycloak.tokenParsed) {
      return false;
    }

    const parsedToken = keycloak.tokenParsed as TokenWithAttributes;

    if (!parsedToken.attributes) {
      return false;
    }

    if (parsedToken.attributes.modulosAcesso.includes(permission)) {
      return true;
    }

    if (!parsedToken.resource_access) {
      return false;
    }

    const resources = parsedToken.resource_access as CustomResources;

    const roles = convertCompressedToRoles((keycloak?.tokenParsed as any)?.compressed || {});
    if (roles.includes(permission)) {
      return true;
    }

    return false;
  }, [keycloak]);

  return hasPermission;
};

function createRolesFromBitMask(module: string, bitmask: number) {
  const moduleAuthorities = [];

  if (bitmask === 444) {
    moduleAuthorities.push(module);
    return moduleAuthorities;
  }

  // Bit 1 (0001) = visualizar
  if ((bitmask & 1) === 1) {
    moduleAuthorities.push(`${module}-visualizar`);
  }

  // Bit 2 (0010) = incluir
  if ((bitmask & 2) === 2) {
    moduleAuthorities.push(`${module}-incluir`);
  }

  // Bit 4 (0100) = alterar
  if ((bitmask & 4) === 4) {
    moduleAuthorities.push(`${module}-alterar`);
  }

  // Bit 8 (1000) = excluir
  if ((bitmask & 8) === 8) {
    moduleAuthorities.push(`${module}-excluir`);
  }

  return moduleAuthorities;
}

function convertCompressedToRoles(compressed: Record<string, number>) {
  const allRoles: string[] = [];

  Object.entries(compressed).forEach(([module, bitmask]) => {
    const moduleRoles = createRolesFromBitMask(module, bitmask);
    allRoles.push(...moduleRoles);
  });

  return allRoles;
}
